/**
 * FeaturedListings Component
 *
 * Horizontal scrollable carousel of featured properties.
 */

import React, { useRef, useState, useEffect } from 'react';
import { collection, query, where, limit, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { Container } from '../../../components/layout';
import { ListingCard } from '../../../components/listings/ListingCard';
import { useSavedListings } from '../../Dashboard/hooks';

interface ListingData {
  id: string;
  title: string;
  photos: Array<{ url: string }>;
  propertyType: string;
  location: { city: string; area?: string; state?: string; country: string };
  pricing: { nightlyRate: number; currency: string };
  rating?: number;
  reviewCount?: number;
  likeCount?: number;
  bedrooms: number;
  beds: number;
  maxGuests: number;
  isFeatured?: boolean;
  hostId?: string;
  host?: {
    displayName: string;
    photoURL?: string;
  };
}

export const FeaturedListings: React.FC = () => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [listings, setListings] = useState<ListingData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toggleSave, savedIds } = useSavedListings();

  // Fetch featured listings from Firestore
  useEffect(() => {
    async function fetchListings() {
      try {
        const q = query(
          collection(db, 'listings'),
          where('isFeatured', '==', true),
          limit(8)
        );
        let snapshot = await getDocs(q);

        // If no featured listings, get top rated ones
        if (snapshot.empty) {
          const fallbackQ = query(collection(db, 'listings'), limit(8));
          snapshot = await getDocs(fallbackQ);
        }

        if (!snapshot.empty) {
          const fetchedListings = snapshot.docs
            .map((doc) => {
              const data = doc.data();
              return {
                id: doc.id,
                title: data.title,
                photos: data.photos || [],
                propertyType: data.propertyType,
                location: {
                  city: data.location?.city || 'Lebanon',
                  area: data.location?.state || 'Lebanon',
                  country: data.location?.country || 'Lebanon',
                },
                pricing: data.pricing,
                rating: data.rating?.average > 0 ? data.rating.average : undefined,
                reviewCount: data.rating?.count > 0 ? data.rating.count : undefined,
                likeCount: data.likeCount > 0 ? data.likeCount : undefined,
                bedrooms: data.capacity?.bedrooms || 1,
                beds: data.capacity?.beds || 1,
                maxGuests: data.capacity?.guests || 2,
                isFeatured: data.isFeatured,
                hostId: data.hostId || '',
                host: data.host ? {
                  displayName: data.host.displayName || '',
                  photoURL: data.host.photoURL,
                } : undefined,
              };
            })
            .sort((a, b) => (b.rating || 0) - (a.rating || 0));
          setListings(fetchedListings);
        }
      } catch (error) {
        console.error('Error fetching listings:', error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchListings();
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320; // Card width + gap
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  return (
    <section className="py-16">
      <Container>
        <div className="flex items-end justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Featured stays
            </h2>
            <p className="text-gray-600">
              Places we'd send a friend to
            </p>
          </div>

          {/* Scroll Controls */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 hover:bg-gray-50 transition-colors"
              aria-label="Scroll left"
            >
              <ChevronLeftIcon className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={() => scroll('right')}
              className="w-10 h-10 rounded-full border border-gray-300 flex items-center justify-center hover:border-gray-400 hover:bg-gray-50 transition-colors"
              aria-label="Scroll right"
            >
              <ChevronRightIcon className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Scrollable Container */}
        <div
          ref={scrollContainerRef}
          className="flex gap-6 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory scrollbar-hide"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {isLoading ? (
            // Loading skeleton
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="flex-none w-[280px] sm:w-[300px] snap-start">
                <div className="bg-gray-200 rounded-xl h-48 animate-pulse" />
                <div className="mt-3 space-y-2">
                  <div className="bg-gray-200 h-4 rounded animate-pulse" />
                  <div className="bg-gray-200 h-4 w-2/3 rounded animate-pulse" />
                </div>
              </div>
            ))
          ) : (
            listings.map((listing) => (
              <div
                key={listing.id}
                className="flex-none w-[280px] sm:w-[300px] snap-start"
              >
                <ListingCard
                  listing={listing}
                  onSave={toggleSave}
                  isSaved={savedIds.includes(listing.id)}
                />
              </div>
            ))
          )}
        </div>

        {/* View All Link */}
        <div className="mt-8 text-center">
          <a
            href="/search?featured=true"
            className="inline-flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700"
          >
            View all featured properties
            <ArrowRightIcon className="w-4 h-4" />
          </a>
        </div>
      </Container>
    </section>
  );
};

// Icons
const ChevronLeftIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const ArrowRightIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

export default FeaturedListings;
